import type { JSX } from 'react';
import { useState } from 'react';
import { MIN_PASSWORD_LENGTH, PublicError, registerAccount } from '../../data/public';
import { FormError, NewPassword, passwordProblem, PublicCard } from './PublicCard';

/**
 * `/register` — an organisation and its owner, together.
 *
 * The one way into the system that is not an invitation, and it grants
 * nothing until the owner opens the emailed link: the account is `pending`,
 * and a pending account cannot invite anybody. So the page ends at "check
 * your email" rather than signing the owner in.
 */
export function Register(): JSX.Element {
  const [accountName, setAccountName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const problem = passwordProblem(password, confirm, MIN_PASSWORD_LENGTH);
    if (problem !== null) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await registerAccount({ accountName, contactPhone, ownerName, ownerEmail, password });
      setSentTo(ownerEmail);
    } catch (err) {
      setError(err instanceof PublicError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  }

  if (sentTo !== null) {
    return (
      <PublicCard subtitle="Check your email">
        <p className="invite__preamble">
          We have sent a link to <strong>{sentTo}</strong>. Open it to confirm the address and
          finish setting up {accountName}. Until then the organisation cannot invite anybody.
        </p>
        <p className="signin__note">The link is good for seven days.</p>
      </PublicCard>
    );
  }

  return (
    <PublicCard subtitle="Register an organisation">
      <form onSubmit={submit}>
        <FormError message={error} />

        <label className="invite__field">
          <span className="invite__label">Organisation name</span>
          <input
            className="invite__input"
            autoComplete="organization"
            required
            value={accountName}
            onChange={(event) => setAccountName(event.target.value)}
          />
          <span className="invite__hint">
            Shown to the clinicians and patients you invite, in their invitation email.
          </span>
        </label>

        <label className="invite__field">
          <span className="invite__label">Phone number for patients</span>
          <input
            className="invite__input"
            type="tel"
            autoComplete="tel"
            required
            value={contactPhone}
            onChange={(event) => setContactPhone(event.target.value)}
          />
          <span className="invite__hint">
            Shown in the app as the number to call for help before their next session. Use a
            line that is answered.
          </span>
        </label>

        <label className="invite__field">
          <span className="invite__label">Your name</span>
          <input
            className="invite__input"
            autoComplete="name"
            required
            value={ownerName}
            onChange={(event) => setOwnerName(event.target.value)}
          />
        </label>

        <label className="invite__field">
          <span className="invite__label">Your email address</span>
          <input
            className="invite__input"
            type="email"
            autoComplete="email"
            required
            value={ownerEmail}
            onChange={(event) => setOwnerEmail(event.target.value)}
          />
          <span className="invite__hint">
            You will administer the organisation: invite clinicians, and set up cohorts.
          </span>
        </label>

        <NewPassword
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          minLength={MIN_PASSWORD_LENGTH}
        />

        <button type="submit" className="primary signin__submit" disabled={busy}>
          {busy ? 'Registering…' : 'Register'}
        </button>

        <p className="signin__note">
          Already registered, or been invited? <a href="/">Sign in</a>.
        </p>
      </form>
    </PublicCard>
  );
}
